import { useEffect } from 'react'

import { useTailgateGrid } from '../hooks/useTailgateGrid'
import { usePermissions } from '@/hooks/usePermissions'

import { Box, Flex, Icon, Tooltip } from '@chakra-ui/react'

import { AssociatedDevices } from '@/components/icons'
import { Button } from '@/components/ui'
import { BUTTON_PRESS } from '@/utils/analytics'

import { GridToolTip } from './GridToolTip'

const TAILGATE_GRID_AUTO_REFRESH_INTERVAL_MS = 3000

export const GridModalDetails = () => {
  const { shouldEnableConfigureTailgateGrid } = usePermissions()

  const {
    clearBoxes,
    setInitialBoxes,
    refreshSnapshot,
    isRefreshingImage,
    isTailgateGridOpen,
    isGridConfigured,
    associateDeviceValues,
    trackTailgateEvent,
  } = useTailgateGrid()

  useEffect(() => {
    if (!isTailgateGridOpen) {
      return
    }

    // Auto-refresh the camera frame every 3s while the modal is open.
    // This reuses the same refresh action as the manual button without
    // adding tracking noise, and cleanup stops refresh immediately on close.
    const intervalId = window.setInterval(() => {
      if (!isRefreshingImage) {
        refreshSnapshot()
      }
    }, TAILGATE_GRID_AUTO_REFRESH_INTERVAL_MS)

    return () => {
      window.clearInterval(intervalId)
    }
  }, [isTailgateGridOpen, isRefreshingImage, refreshSnapshot])

  const onRefreshCamera = () => {
    trackTailgateEvent(`${BUTTON_PRESS} Tailgate Editor - Refresh Camera`)
    refreshSnapshot()
  }
  const onRevertChanges = () => {
    trackTailgateEvent(`${BUTTON_PRESS} Tailgate Editor - Revert Changes`)
    setInitialBoxes()
  }
  const onClearGrid = () => {
    trackTailgateEvent(`${BUTTON_PRESS} Tailgate Editor - Clear Grids`)
    clearBoxes()
  }

  return (
    <Flex direction='column' mb={5}>
      <Flex alignItems='center' direction='row'>
        <Icon as={AssociatedDevices} boxSize='14px' strokeColor='#4DBD6B' />
        <Box color='#1F2023' fontSize='12px' fontWeight='bold' ml={2}>
          Associated Devices
        </Box>
      </Flex>
      <Flex direction='row'>
        <Flex direction='row' flex={1}>
          <DeviceBox
            deviceName={associateDeviceValues?.doorName}
            title='Access Control Device'
          />
          <DeviceBox
            deviceName={associateDeviceValues?.cameraName}
            title='Camera'
          />
          <Box alignSelf='center' justifySelf='center'>
            <GridToolTip />
          </Box>
        </Flex>
        <Flex alignItems='flex-end' justifyContent='flex-end'>
          <Tooltip
            label="Refresh image from camera's live feed"
            openDelay={500}
            shouldWrapChildren
          >
            <Button
              fontSize='12px'
              isLoading={isRefreshingImage}
              loadingText='Refreshing...'
              mr='10px'
              onClick={onRefreshCamera}
              size='sm'
              variant='ghost'
            >
              Refresh Camera
            </Button>
          </Tooltip>
          {isGridConfigured && (
            <Tooltip
              label='Revert previous grids'
              openDelay={500}
              shouldWrapChildren
            >
              <Button
                fontSize='12px'
                isDisabled={!shouldEnableConfigureTailgateGrid}
                mr={2}
                onClick={onRevertChanges}
                size='sm'
                variant='ghost'
              >
                Revert Changes
              </Button>
            </Tooltip>
          )}
          <Tooltip
            label='Remove grids to unset or start again'
            openDelay={500}
            shouldWrapChildren
          >
            <Button
              fontSize='12px'
              isDisabled={!shouldEnableConfigureTailgateGrid}
              mr={2}
              onClick={onClearGrid}
              size='sm'
              variant='ghost'
            >
              Clear Grids
            </Button>
          </Tooltip>
        </Flex>
      </Flex>
    </Flex>
  )
}

interface DeviceBoxIProps {
  title: string
  deviceName: string
}

const DeviceBox = ({ title, deviceName }: DeviceBoxIProps) => {
  return (
    <Box mr={6} mt={3}>
      <Box color='#5C6C7A' fontSize='12px' fontWeight='medium'>
        {title}
      </Box>
      <Box color='#22272B' fontSize='12px' fontWeight='medium'>
        {deviceName}
      </Box>
    </Box>
  )
}
